import { useEffect, useState } from 'react';

import styled, { keyframes } from 'styled-components';

import { IconCheckmark } from '@components/icons/checkmark';
import { SizedRelativeSpinner } from '@components/loading-spinner/loading-spinner';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

const STEP_KEYS = [
  'readingTriggers',
  'walkingBranches',
  'readingTexts',
  'checkingAttributes',
  'traversingJourney',
] as const;

export const STEP_COUNT = STEP_KEYS.length;

export const STEP_INTERVAL_MS = 2500;
export const FINISHING_STEP_MS = 1000;
// How long the all-ticked-off list is held before handing back to the panel.
export const COMPLETED_HOLD_MS = 500;

const ICON_SLOT_SIZE = 16;

export type LoadingChecklistProps = {
  isFinishing?: boolean;
  onFinished?: () => void;
};

export const LoadingChecklist = ({ isFinishing = false, onFinished }: LoadingChecklistProps) => {
  const { t } = useScopedTranslate(locale);
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    // Only the finishing pass may tick off the last step — while the review is
    // still running that step stays spinning.
    const lastStep = isFinishing ? STEP_COUNT : STEP_COUNT - 1;
    const intervalId = setInterval(
      () => setActiveStep((step) => Math.min(step + 1, lastStep)),
      isFinishing ? FINISHING_STEP_MS : STEP_INTERVAL_MS,
    );
    return () => clearInterval(intervalId);
  }, [isFinishing]);

  useEffect(() => {
    if (!isFinishing || activeStep < STEP_COUNT || !onFinished) return;
    const timeoutId = setTimeout(onFinished, COMPLETED_HOLD_MS);
    return () => clearTimeout(timeoutId);
  }, [isFinishing, activeStep, onFinished]);

  return (
    <Stack space={6} align="center">
      <Orb />
      <Stack space={2} align="center">
        <Text font="heading2" text={t('reviewing')} center />
        <Text font="regular14" color="textHelper" text={t('reviewingSubtitle')} center />
      </Stack>
      <Stack space={3}>
        {STEP_KEYS.map((key, index) => {
          const isDone = index < activeStep;
          return (
            <Stack key={key} orientation="horizontal" space={2} align="center">
              <IconSlot>
                {isDone ? (
                  <IconCheckmark size={ICON_SLOT_SIZE} color="elementGreen100" />
                ) : (
                  <SizedRelativeSpinner size={ICON_SLOT_SIZE} />
                )}
              </IconSlot>
              <Text
                font="regular14"
                color={index <= activeStep ? 'textPrimary' : 'textHelper'}
                text={t(key)}
              />
            </Stack>
          );
        })}
      </Stack>
    </Stack>
  );
};

const IconSlot = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: ${pxToRem(ICON_SLOT_SIZE)};
  height: ${pxToRem(ICON_SLOT_SIZE)};
`;

const orbBreathe = keyframes`
  0% {
    transform: scale(0.82);
    box-shadow: 0 0 0 0 ${theme.color.purpleTrans20};
  }
  50% {
    transform: scale(1);
    box-shadow: 0 0 ${pxToRem(24)} ${pxToRem(8)} ${theme.color.purpleTrans20};
  }
  100% {
    transform: scale(0.82);
    box-shadow: 0 0 0 0 ${theme.color.purpleTrans20};
  }
`;

const Orb = styled.div`
  width: ${pxToRem(96)};
  height: ${pxToRem(96)};
  border-radius: 50%;
  background: ${theme.gradient.ai};
  animation: ${orbBreathe} 1.8s ease-in-out infinite;
  margin-top: ${pxToRem(32)};
`;

export const locale = {
  en: {
    reviewing: 'Reviewing your journey',
    reviewingSubtitle: "I'm walking the whole thing end to end, checking fields, texts, and logic.",
    readingTriggers: 'Reading triggers',
    walkingBranches: 'Walking branches end to end',
    readingTexts: 'Reading traveller-facing texts',
    checkingAttributes: 'Checking journey attributes',
    traversingJourney: 'Tracing the whole journey',
  },
};
